import { render } from "preact";
import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import type { JSX } from "preact";
import type { WidgetConfig } from "../api";
import { styles } from "../../../chat-widget/src/styles";
import { COPY } from "../../../chat-widget/src/i18n";
import { launcherHasText, presetOf } from "../../../chat-widget/src/presets";

/**
 * A still of the customer's widget, drawn with the widget's own stylesheet.
 *
 * Before presets the panel drew a hand-made copy of the widget in its own CSS.
 * Eleven shells would make that copy eleven chances to drift, so the preview now
 * imports the one pure function that turns a config into the widget's CSS and
 * applies it to a static copy of the widget's markup, inside a shadow root of
 * its own — exactly how the widget sits on a customer's page. What the owner
 * sees here is what visitors get, down to the byte of CSS.
 *
 * The widget positions itself with `position: fixed`; the stage has a
 * transform, which makes it the containing block for fixed descendants, so the
 * widget lands in the stage's corner rather than the console's.
 */

/** The stage is a small "page": wide enough for the widest preset (terminal, 420px + offset). */
export const STAGE_W = 460;
export const STAGE_H = 680;

const SAMPLE = {
  tr: { visitor: "Siparişim ne zaman gelir?", agent: "Hemen bakıyorum. Sipariş numaranızı yazar mısınız?", agentName: "Ayşe" },
  en: { visitor: "When will my order arrive?", agent: "Let me check. Could you send your order number?", agentName: "Ayşe" },
} as const;

function MockDom({ config, full }: { config: WidgetConfig; full: boolean }): JSX.Element {
  const t = COPY[config.locale] ?? COPY.tr;
  const sample = SAMPLE[config.locale] ?? SAMPLE.tr;
  const initial = config.title.trim().charAt(0).toLocaleUpperCase("tr") || "?";
  const text = launcherHasText(config) ? config.launcherText.trim() || t.chat : null;
  return (
    <>
      <div class="panel" lang={config.locale}>
        <div class="header">
          <div class="avatar">{config.logoUrl ? <img src={config.logoUrl} alt="" /> : <span>{initial}</span>}</div>
          <div>
            <h2>{config.title || t.team}</h2>
            <p class="status">
              <span class="dot" />
              {t.online}
            </p>
          </div>
          <button class="close" tabIndex={-1}>
            ✕
          </button>
        </div>
        <div class="messages">
          {config.greeting ? (
            <div class="row">
              <div>
                <div class="bubble">{config.greeting}</div>
              </div>
            </div>
          ) : null}
          {full && config.quickReplies.length > 0 ? (
            <div class="quick-replies">
              {config.quickReplies.map((q) => (
                <button key={q} class="quick-reply" tabIndex={-1}>
                  {q}
                </button>
              ))}
            </div>
          ) : null}
          {full && config.faq.length > 0 ? (
            <div class="faq">
              <strong class="faq-title">{t.faqTitle}</strong>
              {config.faq.slice(0, 2).map((item) => (
                <details key={item.q} class="faq-item">
                  <summary tabIndex={-1}>{item.q}</summary>
                </details>
              ))}
            </div>
          ) : null}
          <div class="row visitor">
            <div>
              <div class="bubble">{sample.visitor}</div>
              <div class="meta">{t.you} · 14:02</div>
            </div>
          </div>
          <div class="row agent">
            <span class="op-avatar" style={{ background: "var(--cc-primary)", color: "var(--cc-on-primary)" }}>
              {sample.agentName.charAt(0)}
            </span>
            <div>
              <div class="bubble">{sample.agent}</div>
              <div class="meta">{sample.agentName} · 14:03</div>
            </div>
          </div>
          {full && config.showTypingIndicator ? (
            <div class="row agent">
              <div class="bubble typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          ) : null}
        </div>
        <div class="composer">
          {config.attachmentsEnabled ? (
            <span class="attach">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          ) : null}
          <textarea rows={1} placeholder={config.placeholder} readOnly tabIndex={-1} />
          <button class="send" tabIndex={-1}>
            ➤
          </button>
        </div>
      </div>
      <button class="launcher" tabIndex={-1}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {text ? <span class="launcher-label">{text}</span> : null}
      </button>
    </>
  );
}

export function WidgetMock({
  config,
  scale,
  full = false,
  label,
}: {
  config: WidgetConfig;
  scale: number;
  /** The live preview shows quick replies, FAQ and typing; gallery cards stay simple. */
  full?: boolean;
  label?: string;
}): JSX.Element {
  const hostRef = useRef<HTMLSpanElement>(null);
  const parts = useRef<{ style: HTMLStyleElement; mount: HTMLDivElement } | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!parts.current) {
      const shadow = host.attachShadow({ mode: "open" });
      const style = document.createElement("style");
      const mount = document.createElement("div");
      shadow.append(style, mount);
      parts.current = { style, mount };
    }
    host.setAttribute("data-preset", presetOf(config));
    parts.current.style.textContent = styles(config);
    render(<MockDom config={config} full={full} />, parts.current.mount);
  });

  useEffect(
    () => () => {
      if (parts.current) render(null, parts.current.mount);
    },
    [],
  );

  return (
    <span
      class="wmock"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      style={{ width: `${Math.round(STAGE_W * scale)}px`, height: `${Math.round(STAGE_H * scale)}px` }}
    >
      <span class="wmock-stage" style={{ width: `${STAGE_W}px`, height: `${STAGE_H}px`, transform: `scale(${scale})` }}>
        <span ref={hostRef} class="wmock-host" />
      </span>
    </span>
  );
}
